import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useResetPassword } from '@/hooks/useAuth';
import PasswordInput from '@/components/PasswordInput';
import { resetPasswordErrorMessage, passwordRequirementsMessage } from '@/lib/errors';
import { MIN_PASSWORD_LENGTH, validatePasswordStrength } from 'shared';

function BackArrowIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M19 12H5m0 0l7 7m-7-7l7-7" />
    </svg>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // Guardians' reset links carry conta=ee; everyone else's is a participant's.
  const resetPassword = useResetPassword(
    searchParams.get('conta') === 'ee' ? 'guardian' : 'participant',
  );

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Link de reposição inválido ou expirado');
    }
  }, [token]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Link de reposição inválido ou expirado');
      return;
    }

    if (password !== confirmPassword) {
      setError('As palavras-passe não coincidem');
      return;
    }

    const passwordError = passwordRequirementsMessage(validatePasswordStrength(password));
    if (passwordError) {
      setError(passwordError);
      return;
    }

    resetPassword.mutate(
      { token, newPassword: password },
      {
        onSuccess: () => setSuccess(true),
      }
    );
  };

  const renderShell = (children: React.ReactNode) => (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <span
        className="phone-bg-blob"
        style={{
          width: 260,
          height: 260,
          top: -60,
          right: -80,
          backgroundColor: 'hsla(var(--accent-purple), 0.12)',
        }}
        aria-hidden="true"
      />
      <div className="relative z-10 mx-auto w-full max-w-md px-6 pt-8 pb-12">
        <button
          type="button"
          className="back-link"
          onClick={() => navigate('/login')}
          aria-label=""
        >
          <BackArrowIcon />
          Voltar
        </button>
        {children}
      </div>
    </div>
  );

  if (success) {
    return (
      <>
        <Helmet>
          <title>Repor palavra-passe | AICD</title>
        </Helmet>
        {renderShell(
          <>
            <header className="mt-2">
              <h1 className="text-[26px] font-extrabold tracking-tight text-foreground">
                Palavra-passe reposta com sucesso
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Já pode iniciar sessão com a nova palavra-passe.
              </p>
            </header>

            <div
              role="status"
              aria-live="polite"
              className="mt-8 rounded-xl border border-available/30 bg-available-soft px-4 py-4 text-sm font-medium text-available"
            >
              A sua palavra-passe foi reposta com sucesso.
            </div>

            <button
              onClick={() => navigate('/login')}
              className="btn-primary w-full mt-6"
            >
              Iniciar sessão
            </button>
          </>
        )}
      </>
    );
  }

  if (!token) {
    return (
      <>
        <Helmet>
          <title>Repor palavra-passe | AICD</title>
        </Helmet>
        {renderShell(
          <>
            <header className="mt-2">
              <h1 className="text-[26px] font-extrabold tracking-tight text-foreground">
                Link de reposição inválido
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Solicite um novo link de reposição de palavra-passe abaixo.
              </p>
            </header>

            <div
              role="alert"
              aria-live="assertive"
              className="mt-8 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-4 text-sm font-medium text-destructive"
            >
              Este link de reposição é inválido ou expirou. Solicite um novo.
            </div>

            <Link
              to="/forgot-password"
              className="btn-primary w-full mt-6 block text-center"
            >
              Solicitar novo link
            </Link>
          </>
        )}
      </>
    );
  }

  const hasError = !!(error || resetPassword.isError);

  return (
    <>
      <Helmet>
        <title>Reset Password | AICD</title>
      </Helmet>
      {renderShell(
        <>
          <header className="mt-2">
            <h1 className="text-[26px] font-extrabold tracking-tight text-foreground">
              Repor palavra-passe
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Introduza a nova palavra-passe abaixo.
            </p>
          </header>

          <form
            className="mt-8 flex flex-col gap-4"
            onSubmit={handleSubmit}
            aria-busy={resetPassword.isPending}
            noValidate
          >
            <div role="alert" aria-live="assertive" aria-atomic="true">
              {hasError && (
                <div
                  id="reset-password-error"
                  className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive"
                >
                  {error || resetPasswordErrorMessage(resetPassword.error)}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="password" className="label">
                Nova palavra-passe
              </label>
              <PasswordInput
                id="password"
                autoComplete="new-password"
                required
                placeholder={`Mín. ${MIN_PASSWORD_LENGTH} caracteres`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={hasError || undefined}
                aria-describedby={`password-hint${hasError ? ' reset-password-error' : ''}`}
              />
              <p id="password-hint" className="form-hint mt-1.5">
                {`Pelo menos ${MIN_PASSWORD_LENGTH} caracteres, com maiúsculas, minúsculas e um número.`}
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                Confirmar nova palavra-passe
              </label>
              <PasswordInput
                id="confirmPassword"
                revealLabel="confirmação da palavra-passe"
                autoComplete="new-password"
                required
                placeholder="Repita a nova palavra-passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                aria-invalid={hasError || undefined}
                aria-describedby={hasError ? 'reset-password-error' : undefined}
              />
            </div>

            <button
              type="submit"
              disabled={resetPassword.isPending}
              className="btn-primary w-full mt-2"
            >
              {resetPassword.isPending ? 'A repor…' : 'Repor palavra-passe'}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            <Link to="/login" className="font-semibold text-primary">
              Voltar ao início de sessão
            </Link>
          </p>
        </>
      )}
    </>
  );
}
